import type {
  ReferenceCheck as ReferenceCheckType,
  Reference as ReferenceType,
  SeekDateTime,
  Maybe,
} from '@seek/ca-graphql-schema/types';
import { ReferenceChecks } from '@seek/profile-read-ui';
import { requiredSelection } from '@seek/validators-js';
import { useTranslations } from '@vocab/react';
import { RadioGroup, RadioItem, Stack } from 'braid-design-system';
import { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { createFieldValidator } from 'src/forms/validator';
import { useEditorDrawer } from 'src/ui/EditorDrawer/EditorDrawer';

import { useConfig } from '../../context/config';

import translations from './.vocab';

type ShortAbsoluteLabelOnly = Pick<SeekDateTime, 'shortAbsoluteLabel'>;
type ShortDateFields = {
  fromDate: ShortAbsoluteLabelOnly;
  toDate?: Maybe<ShortAbsoluteLabelOnly>;
  referenceDate: ShortAbsoluteLabelOnly;
};

type ModifiedReference = Omit<
  ReferenceType,
  'fromDate' | 'toDate' | 'referenceDate'
> &
  ShortDateFields;

export type ModifiedReferenceCheck = Omit<ReferenceCheckType, 'references'> & {
  references: ModifiedReference[];
};

interface ReferencesListProps {
  referenceChecks: ModifiedReferenceCheck[];
  onFormDirtyChange?: (isDirty: boolean) => void;
}

export const LOCAL_STORAGE_KEY = 'lastApplication';
export const REFERENCES_SELECTION_NAMESPACE = 'references.selection';
export const REFERENCES_ID_NAMESPACE = 'references.id';
export const REFERENCES_NAMESPACE = 'references';
export const SESSION_STORAGE_KEY = 'setReferenceOnce';
export const removeSessionStorage = () =>
  sessionStorage.removeItem(SESSION_STORAGE_KEY);

export const ReferencesList = ({
  referenceChecks,
  onFormDirtyChange,
}: ReferencesListProps) => {
  const { t } = useTranslations(translations);
  const id = referenceChecks[0].id; // TODO: Handle multiple reference checks
  const { control, watch, setValue, getValues } = useFormContext();
  const { config } = useConfig();
  const validate = createFieldValidator(
    [requiredSelection],
    {},
    config.LANGUAGE,
  );
  const referenceValue = watch(REFERENCES_SELECTION_NAMESPACE);
  const { onEditorOpen } = useEditorDrawer();

  useEffect(() => {
    const hasRunEffect = sessionStorage.getItem('setReferenceOnce');
    if (!hasRunEffect) {
      const storedLocalStorage = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (storedLocalStorage) {
        const parsedItem = JSON.parse(storedLocalStorage);

        if (parsedItem.reference.id === id) {
          setValue(
            REFERENCES_SELECTION_NAMESPACE,
            parsedItem.reference.selection,
          );
        } else {
          localStorage.removeItem(LOCAL_STORAGE_KEY);
          setValue(REFERENCES_SELECTION_NAMESPACE, 'include');
        }
      } else {
        setValue(REFERENCES_SELECTION_NAMESPACE, 'include');
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Controller
        name={REFERENCES_SELECTION_NAMESPACE}
        control={control}
        defaultValue={getValues(REFERENCES_SELECTION_NAMESPACE)}
        rules={{ validate }}
        render={({ field, fieldState }) => (
          <RadioGroup
            {...field}
            id={field.name}
            label=""
            tone={fieldState.invalid ? 'critical' : 'positive'}
            message={fieldState.error?.message}
            onChange={(event) => {
              const value = event.currentTarget.value;
              field.onChange(value);

              onFormDirtyChange?.(true);
            }}
          >
            <RadioItem label={t('Include references')} value="include" />
            <RadioItem
              label={t('Don’t include references')}
              value="dont-include"
            />
          </RadioGroup>
        )}
      />
      <Controller
        name={REFERENCES_ID_NAMESPACE}
        control={control}
        defaultValue={id}
        render={() => <></>}
      />
      {(referenceValue === 'include' || referenceValue === '') && (
        <Stack component="ul" space="small">
          <ReferenceChecks
            referenceChecks={referenceChecks}
            onEditCard={onEditorOpen}
          />
        </Stack>
      )}
    </>
  );
};
